import { StyleSheet, Text, type StyleProp, type TextProps, type TextStyle } from 'react-native';

import { colors, fonts, fontSizes } from '@/constants/theme';
import { fitLineHeight } from '@/lib/textFit';

type Size = '2xl' | '3xl' | '4xl' | '5xl';
type Tone = 'default' | 'inverse' | 'accent';

export interface HeadingProps extends Omit<TextProps, 'style'> {
  children: string;
  size?: Size;
  tone?: Tone;
  style?: StyleProp<TextStyle>;
}

/**
 * Display headline in FK Screamer, set the way marketpryce.com sets it:
 * always uppercase, +0.5 tracking, line-height 1.0, Deep Green on light
 * surfaces (`inverse` for Frost on dark cards).
 *
 * FK Screamer is a condensed face drawn for big caps. Below 24pt it reads
 * scrunched and sentence case fights the letterforms, so there is no
 * smaller size and no sentence-case option. A sub-head that needs to be
 * smaller or sentence-case is Druk Wide Medium (`fonts.subhead`) in a
 * plain Text, not a smaller Heading.
 */
export function Heading({
  children,
  size = '3xl',
  tone = 'default',
  style,
  ...rest
}: HeadingProps) {
  return (
    <Text accessibilityRole="header" style={fitLineHeight([...headingStyle(size, tone), style])} {...rest}>
      {children}
    </Text>
  );
}

/**
 * The Heading's resolved style, for the one case a Heading can't be used
 * directly: copy with nested runs (HighlightedText's `**$50**` spans).
 * Heading types `children` as string on purpose, so this is the escape
 * hatch rather than loosening that.
 */
export function headingStyle(size: Size = '3xl', tone: Tone = 'default'): TextStyle[] {
  return [styles.base, SIZES[size], TONES[tone]];
}

// Tracking is +0.5 across the board (site rule; negative tracking collapsed
// "JEFF C." into "JEFFC."). Line-height 1.0: uppercase has no descenders
// and FK Screamer's caps are 0.75em, so 1.0 leaves air without the loose,
// double-spaced look 1.1–1.2 gave multi-line headlines.
const SIZES: Record<Size, TextStyle> = {
  '2xl': { fontSize: fontSizes['2xl'], lineHeight: fontSizes['2xl'] },
  '3xl': { fontSize: fontSizes['3xl'], lineHeight: fontSizes['3xl'] },
  '4xl': { fontSize: fontSizes['4xl'], lineHeight: fontSizes['4xl'] },
  '5xl': { fontSize: fontSizes['5xl'], lineHeight: fontSizes['5xl'] },
};

const TONES: Record<Tone, TextStyle> = {
  default: { color: colors.deepGreen },
  inverse: { color: colors.textInverse },
  accent: { color: colors.electricGreen },
};

const styles = StyleSheet.create({
  base: {
    fontFamily: fonts.display,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
});
